import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { CustomCron } from '@components/ui-cron/cron.models';
import {
    ClientErrorCodes,
    FeatureFlag,
    ITriggerUpsert,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    MapperService,
    ProcessService,
    QueueDefinitionService,
    SettingService,
} from '@services';
import { CalendarService } from '@services/calendar.service';
import { ODataService } from '@services/generics/odata.service';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest/models';

import {
    forkJoin,
    of,
} from 'rxjs';
import {
    map,
    switchMap,
} from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class TriggerUpsertPageService extends ODataService<ITriggerUpsert, number> {
    protected _endpoint = '/odata/ProcessSchedules';

    public get timezoneId() {
        return this._appState.settings.get(Setting.timezoneId)!;
    }

    public get isMultipleCalendarsEnabled() {
        return this._appState.settings.isFeatureEnabled(FeatureFlag.MultipleNonWorkingCalendars);
    }

    constructor(
        private _translateService: TranslateService,
        private _appState: AppStateFacade,
        private _settingService: SettingService,
        private _processService: ProcessService,
        private _queueService: QueueDefinitionService,
        private _calendarService: CalendarService,
        private _snack: UiSnackBarService,
        http: HttpClient,
    ) {
        super(http, MapperService.triggerUpsertMap);
    }

    public processesSuggest$ = (searchTerm: string | undefined, fetchSize: number | undefined, skipCount: number) =>
        this._appState.user.folder
            .value$
            .pipe(
                switchMap(() => this._processService
                    .suggest(searchTerm, fetchSize, skipCount),
                ),
            )

    public getTimezones = () =>
        this._settingService
            .getTimezones()
            .pipe(
                map(timezones =>
                    timezones.map(tz => ({
                        id: tz.Value,
                        text: tz.Name,
                    })),
                ),
            )

    public getInstanceData = (id: number, timezones: ISuggestValue[]) =>
        super.getInstance(id)
            .pipe(
                map(trigger => {
                    const timezone = timezones
                        .find(t => t.id === trigger.timezone[0].id)!;
                    return {
                        ...trigger,
                        timezone: [timezone],
                    };
                }),
                switchMap(trigger =>
                    // process details and executorRobots are not returned on trigger endpoint
                    forkJoin({
                        process: this.getProcess(trigger.process[0].id as number),
                        // if startStrategy is specific robots
                        robots: trigger.startStrategy === 0 ?
                            this._getExecutorRobots(id) :
                            of([]),
                    })
                        .pipe(
                            map(({ process, robots }) => {
                                trigger.process[0].data = process;
                                trigger.executorRobots = robots;
                                return trigger;
                            }),
                        ),
                ),
            )

    public getProcess = (id: number) =>
        this._processService
            .getInstance(id)

    public getQueueDefinition = (id: number) =>
        this._queueService
            .getInstance(id)

    public getDefaultCalendar = () =>
        this._settingService
            .getCalendar()

    public getCalendarNames = () =>
        this._calendarService
            .get()
            .pipe(
                map((calendars) => calendars.data.map(
                    calendar => ({
                        id: calendar.calendarId,
                        text: calendar.calendarName,
                    }),
                )),
            )

    public update(id: number, formValues: ITriggerUpsert) {
        const payload = { ...formValues, id };
        return super.update(id, payload);
    }

    public parseCron(startProcessCronDetails?: ITriggerUpsert['startProcessCronDetails']): CustomCron {
        let flatCron = null;
        try {
            if (startProcessCronDetails) {
                flatCron = JSON.parse(startProcessCronDetails);
            }
        } catch (e) {
            this._snack.error(`${this._translateService.instant('json_parse_fail')} (#${ClientErrorCodes.InvalidJSON})`);
        }

        return new CustomCron(flatCron);
    }

    private _getExecutorRobots(triggerId: number) {
        return this._http.get<{ value: number[] }>(`${this._endpoint
            }/UiPath.Server.Configuration.OData.GetRobotIdsForSchedule(key=${triggerId})`)
            .pipe(
                map(({ value }) =>
                    value.map(id => ({ id })),
                ),
            );
    }
}
